/**
 * The one way to read notifications in the UI.
 *
 * Three surfaces show this endpoint — the navbar bell, the dashboard's "Needs
 * attention" card, and the dashboard's "Unread notifications" stat — and each
 * of them separately had to remember to subtract dismissals. Two of the three
 * forgot at some point: the card never filtered at all, and the stat kept
 * counting items the user had already cleared, which reads as "the dismiss
 * button doesn't work".
 *
 * Fetching and filtering therefore live together here. A caller cannot get the
 * unfiltered list by accident, so a fourth surface can't reintroduce the bug.
 *
 * One query key across all three, so react-query dedupes what used to be three
 * requests for the same data. Each caller keeps its own refetch interval —
 * observers are independent, so the bell's 5 minutes and the dashboard's
 * user-set cadence coexist.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { listNotifications, type Notification } from "@/api/notifications";

import { useDismissedNotifications } from "./dismissedNotifications";

export function useNotifications(options?: { refetchInterval?: number | false }) {
  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: listNotifications,
    ...(options?.refetchInterval !== undefined
      ? { refetchInterval: options.refetchInterval }
      : {}),
  });

  const { dismiss, dismissAll, restore, isDismissed } = useDismissedNotifications();

  const items: Notification[] = useMemo(
    () => (query.data?.items ?? []).filter((n) => !isDismissed(n.id)),
    [query.data, isDismissed],
  );

  return {
    items,
    isLoading: query.isLoading,
    dismiss,
    dismissAll,
    restore,
  };
}
